import React, { useEffect, useState, useMemo } from 'react';
import QRCode from 'qrcode';
import { doc, onSnapshot } from 'firebase/firestore';
import { 
  CheckCircle2, Clock, XCircle, Copy, Check, 
  ExternalLink, ChevronDown, ChevronUp, ShieldCheck, 
  Smartphone, Sparkles, RefreshCw, AlertCircle, ArrowLeft,
  Info, QrCode
} from 'lucide-react';
import { auth, db } from '../utils/firebase';
import { submitUpiPayment, getUpiPaymentStatus } from '../services/upiService';

const UPI_ID = 'gaikarvishal@axl';
const PAYEE = 'VISHAL SHANKAR GAIKAR';
const AMOUNTS = { monthly: 49, annual: 299, lifetime: 799 };
const PLAN_LABELS = { monthly: 'Monthly Pass', annual: 'Annual Pass', lifetime: 'Lifetime Access' };
const PENDING_STORAGE_KEY = 'webiptv_pending_upi';

export default function UpiCheckout({ planId = 'monthly', onVerified }) {
  const [qr, setQr] = useState('');
  const [utr, setUtr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showMobileQr, setShowMobileQr] = useState(false);

  const amount = AMOUNTS[planId] || 49;
  const planLabel = PLAN_LABELS[planId] || 'Premium Pass';

  const upiQuery = useMemo(() => {
    return new URLSearchParams({
      pa: UPI_ID,
      pn: PAYEE,
      am: amount.toFixed(2),
      cu: 'INR',
      tn: `Web IPTV Player ${planLabel}`
    }).toString();
  }, [amount, planLabel]);

  const standardUri = `upi://pay?${upiQuery}`;
  const phonepeUri = `phonepe://pay?${upiQuery}`;
  const gpayUri = `gpay://upi/pay?${upiQuery}`;
  const paytmUri = `paytmmp://pay?${upiQuery}`;

  // Restore existing pending payment from storage if exists for current user
  useEffect(() => {
    try {
      const saved = localStorage.getItem(PENDING_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.paymentId && parsed?.planId === planId) {
          setReceipt(parsed);
        }
      }
    } catch (e) {}
  }, [planId]);

  // Generate High-Quality QR Code
  useEffect(() => {
    let active = true;
    QRCode.toDataURL(standardUri, {
      width: 220,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: {
        dark: '#030712',
        light: '#ffffff'
      }
    })
      .then(value => { if (active) setQr(value); })
      .catch(() => { if (active) setError('Could not render QR code. Please use the mobile payment links.'); });
    return () => { active = false; };
  }, [standardUri]);

  // Real-Time onSnapshot Listener for Instant Auto-Activation
  useEffect(() => {
    if (!receipt?.paymentId) return;

    const unsub = onSnapshot(doc(db, 'upiPayments', receipt.paymentId), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (data?.status) {
        setReceipt(prev => ({ ...(prev || {}), status: data.status, paymentId: receipt.paymentId }));

        if (data.status === 'approved') {
          try { localStorage.removeItem(PENDING_STORAGE_KEY); } catch (e) {}
          if (onVerified) {
            setTimeout(() => onVerified(), 1200);
          } else {
            setTimeout(() => window.location.reload(), 1200);
          }
        }
      }
    }, (err) => {
      console.debug('Snapshot listener idle, waiting for server approval signal:', err?.message);
    });

    return () => unsub();
  }, [receipt?.paymentId, onVerified]);

  const handleCopyUpi = async () => {
    try {
      await navigator.clipboard.writeText(UPI_ID);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError(`Please copy manually: ${UPI_ID}`);
    }
  };

  const handleUtrChange = (e) => {
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 12);
    setUtr(cleaned);
    if (error) setError('');
  };

  const submitUtr = async (event) => {
    if (event) event.preventDefault();
    setError('');

    if (!auth.currentUser) {
      setError('Please sign in before submitting your payment.');
      return;
    }

    if (!/^\d{12}$/.test(utr)) {
      setError('Please enter the full 12-digit numeric UPI reference (UTR).');
      return;
    }

    setBusy(true);
    try {
      const receiptData = await submitUpiPayment(planId, utr);
      setReceipt(receiptData);

      try {
        localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify({
          paymentId: receiptData.paymentId,
          planId,
          utr,
          amount,
          status: receiptData.status || 'pending',
          timestamp: Date.now()
        }));
      } catch (e) {}

      if (receiptData.status === 'approved') {
        if (onVerified) onVerified();
        else window.location.reload();
      }
    } catch (err) {
      setError(err.message || 'Could not submit payment. Please verify your UTR.');
    } finally {
      setBusy(false);
    }
  };

  const checkStatusManual = async () => {
    if (!receipt?.paymentId) return;
    setBusy(true);
    setError('');
    try {
      const statusData = await getUpiPaymentStatus(receipt.paymentId);
      setReceipt(statusData);
      if (statusData.status === 'approved') {
        try { localStorage.removeItem(PENDING_STORAGE_KEY); } catch (e) {}
        if (onVerified) onVerified();
        else window.location.reload();
      }
    } catch (err) {
      setError(err.message || 'Could not verify status. Please wait a moment.');
    } finally {
      setBusy(false);
    }
  };

  const resetPayment = () => {
    try { localStorage.removeItem(PENDING_STORAGE_KEY); } catch (e) {}
    setReceipt(null);
    setUtr('');
    setError('');
  };

  return (
    <div className="rounded-2xl border border-sky-500/25 bg-slate-950/70 p-3 sm:p-4 text-white shadow-xl space-y-3" aria-label="UPI payment">
      {/* Mini Header Bar */}
      <div className="flex items-center justify-between pb-2 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-purple-600 to-sky-500 flex items-center justify-center font-black text-xs shadow-md">
            ₹
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-extrabold text-xs text-white">Instant UPI</span>
            <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              0% Extra Fees
            </span>
            <span className="text-[10px] text-slate-400 font-mono hidden sm:inline">· Direct P2P</span>
          </div>
        </div>
        <div className="text-right flex items-center gap-1.5">
          <span className="text-sm font-black text-emerald-400">₹{amount}</span>
          <span className="text-[9px] text-slate-400">one-time</span>
        </div>
      </div>

      {/* ── VIEW 1: ACTIVE STATUS / VERIFYING VIEW ── */}
      {receipt ? (
        <div className="space-y-3 text-center py-2" role="status">
          {receipt.status === 'approved' ? (
            <div className="space-y-2">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 border-2 border-emerald-500 text-emerald-400 flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/30 animate-bounce">
                <CheckCircle2 size={28} />
              </div>
              <h4 className="text-sm font-extrabold text-emerald-400">Payment Verified & Activated!</h4>
              <p className="text-[11px] text-slate-300">Unlocking full streaming libraries now...</p>
            </div>
          ) : receipt.status === 'rejected' ? (
            <div className="space-y-2">
              <div className="w-12 h-12 rounded-full bg-rose-500/20 border-2 border-rose-500 text-rose-400 flex items-center justify-center mx-auto shadow-lg shadow-rose-500/30">
                <XCircle size={28} />
              </div>
              <h4 className="text-sm font-extrabold text-rose-400">Payment Could Not Be Verified</h4>
              <p className="text-[11px] text-slate-300">Please check your 12-digit UTR and try again.</p>
              <button 
                onClick={resetPayment}
                className="px-3.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-bold transition text-white"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-400 animate-ping" />
                <span className="text-xs font-bold text-amber-300">Live Auto-Activation Active</span>
              </div>

              <div className="space-y-0.5">
                <h4 className="text-sm font-extrabold text-white">Payment Submitted · Verifying Bank Ledger</h4>
                <p className="text-[11px] text-slate-400 max-w-sm mx-auto">
                  This dialog will <strong className="text-white">auto-activate instantly</strong> once your ₹{amount} transfer reflects.
                </p>
              </div>

              {receipt.paymentId && (
                <div className="p-2 rounded-xl bg-white/5 border border-white/10 text-left font-mono text-[10px] space-y-0.5 max-w-sm mx-auto">
                  <div className="text-slate-400 uppercase font-bold text-[9px]">Reference Hash:</div>
                  <div className="text-slate-200 truncate select-all">{receipt.paymentId}</div>
                </div>
              )}

              <div className="flex gap-2 max-w-xs mx-auto">
                <button
                  type="button"
                  onClick={checkStatusManual}
                  disabled={busy}
                  className="flex-1 py-2 px-3 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-md shadow-sky-500/20 transition disabled:opacity-50"
                >
                  <RefreshCw size={13} className={busy ? 'animate-spin' : ''} />
                  <span>{busy ? 'Checking...' : 'Check Status'}</span>
                </button>
                <button
                  type="button"
                  onClick={resetPayment}
                  className="py-2 px-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 hover:text-white text-xs font-semibold transition"
                >
                  Edit UTR
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ── VIEW 2: UNIFIED DESKTOP SIDE-BY-SIDE + SMART MOBILE LAYOUT ── */
        <div className="space-y-3">
          {/* Desktop: Side-by-Side (QR on Left, Action Form on Right) */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3.5 items-start">
            
            {/* ── LEFT COLUMN (Desktop): QR CODE CARD ── */}
            <div className="hidden md:flex md:col-span-5 flex-col items-center justify-center bg-white/5 border border-white/10 rounded-2xl p-2.5 space-y-2">
              <div className="w-[150px] h-[150px] p-1.5 rounded-xl bg-white shadow-md flex items-center justify-center">
                {qr ? (
                  <img
                    src={qr}
                    alt={`UPI QR for ₹${amount} to ${PAYEE}`}
                    className="w-[138px] h-[138px] rounded-lg"
                  />
                ) : (
                  <div className="w-[138px] h-[138px] flex items-center justify-center text-slate-800 text-[10px]">
                    Loading QR...
                  </div>
                )}
              </div>
              <div className="text-center space-y-0.5">
                <div className="text-[10px] text-slate-300 font-bold">Scan with PhonePe / GPay / Any UPI</div>
                <div className="text-[9px] text-emerald-400 font-medium flex items-center justify-center gap-1">
                  <ShieldCheck size={11} />
                  <span>{PAYEE}</span>
                </div>
              </div>
            </div>

            {/* ── RIGHT COLUMN (Desktop) / FULL COLUMN (Mobile): ACTIONS ── */}
            <div className="md:col-span-7 space-y-2.5">
              
              {/* Mobile Quick Launcher Grid (Front and Center on Mobile!) */}
              <div className="space-y-1">
                <span className="block text-[10px] text-slate-400 font-semibold md:hidden">
                  Tap to pay directly in your app:
                </span>
                <span className="hidden md:block text-[10px] text-slate-400 font-semibold">
                  Or launch app directly:
                </span>
                <div className="grid grid-cols-2 gap-1.5 text-[11px] font-bold">
                  <a
                    href={phonepeUri}
                    className="py-1.5 px-2.5 rounded-xl bg-purple-600/30 hover:bg-purple-600/45 border border-purple-500/50 text-purple-200 flex items-center justify-center gap-1 transition active:scale-95 text-center truncate"
                  >
                    <span>PhonePe</span>
                    <ExternalLink size={10} className="shrink-0" />
                  </a>
                  <a
                    href={gpayUri}
                    className="py-1.5 px-2.5 rounded-xl bg-blue-600/30 hover:bg-blue-600/45 border border-blue-500/50 text-blue-200 flex items-center justify-center gap-1 transition active:scale-95 text-center truncate"
                  >
                    <span>Google Pay</span>
                    <ExternalLink size={10} className="shrink-0" />
                  </a>
                  <a
                    href={paytmUri}
                    className="py-1.5 px-2.5 rounded-xl bg-sky-600/30 hover:bg-sky-600/45 border border-sky-500/50 text-sky-200 flex items-center justify-center gap-1 transition active:scale-95 text-center truncate"
                  >
                    <span>Paytm</span>
                    <ExternalLink size={10} className="shrink-0" />
                  </a>
                  <a
                    href={standardUri}
                    className="py-1.5 px-2.5 rounded-xl bg-emerald-600/30 hover:bg-emerald-600/45 border border-emerald-500/50 text-emerald-200 flex items-center justify-center gap-1 transition active:scale-95 text-center truncate"
                  >
                    <span>⚡ Any UPI</span>
                    <ExternalLink size={10} className="shrink-0" />
                  </a>
                </div>
              </div>

              {/* Copy UPI ID Pill (Compact) */}
              <div className="flex items-center justify-between p-2 rounded-xl bg-white/5 border border-white/10 text-xs">
                <div className="overflow-hidden mr-1">
                  <span className="block text-[9px] text-slate-400 uppercase font-bold">UPI ID (VPA)</span>
                  <span className="font-mono text-[11px] font-bold text-sky-300 truncate block select-all">{UPI_ID}</span>
                </div>
                <button
                  type="button"
                  onClick={handleCopyUpi}
                  className="px-2.5 py-1 rounded-lg bg-sky-500/20 hover:bg-sky-500/30 border border-sky-400/40 text-sky-300 text-[10px] font-bold flex items-center gap-1 transition shrink-0 active:scale-95"
                >
                  {copied ? (
                    <>
                      <Check size={11} className="text-emerald-400" />
                      <span>Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy size={11} />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>

              {/* Mobile-Only Collapsible QR Toggle (Saves 200px of scrolling on phone!) */}
              <div className="md:hidden">
                <button
                  type="button"
                  onClick={() => setShowMobileQr(!showMobileQr)}
                  className="w-full py-1.5 px-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 text-[10px] font-semibold flex items-center justify-between transition"
                >
                  <span className="flex items-center gap-1.5">
                    <QrCode size={13} className="text-sky-400" />
                    <span>Scan QR with another phone instead?</span>
                  </span>
                  {showMobileQr ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                {showMobileQr && (
                  <div className="pt-2 flex flex-col items-center">
                    <div className="w-[160px] h-[160px] p-2 rounded-xl bg-white shadow-lg flex items-center justify-center">
                      {qr && <img src={qr} alt={`QR for ₹${amount}`} className="w-[144px] h-[144px]" />}
                    </div>
                    <span className="text-[10px] text-slate-400 mt-1">{PAYEE}</span>
                  </div>
                )}
              </div>

              {/* UTR Submission Input & Button */}
              <form onSubmit={submitUtr} className="space-y-2">
                <div className="space-y-1">
                  <div className="flex justify-between items-center text-[10px]">
                    <label htmlFor="upi-utr" className="font-bold text-slate-200 flex items-center gap-1">
                      <span>12-Digit Reference No. (UTR)</span>
                      <span className="text-rose-400">*</span>
                    </label>
                    <span className="font-mono text-slate-400">{utr.length}/12</span>
                  </div>
                  <div className="relative">
                    <input
                      id="upi-utr"
                      value={utr}
                      onChange={handleUtrChange}
                      required
                      pattern="[0-9]{12}"
                      maxLength={12}
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="e.g. 528912345678"
                      className="w-full rounded-xl bg-slate-900 border border-sky-400/40 focus:border-sky-400 focus:outline-none p-2.5 text-sm font-mono tracking-widest text-white placeholder:text-slate-600 transition"
                    />
                    {utr.length === 12 && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-400">
                        <CheckCircle2 size={16} />
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={busy || utr.length !== 12}
                  className="w-full py-2.5 px-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white text-xs font-extrabold flex items-center justify-center gap-1.5 shadow-md shadow-emerald-500/25 transition disabled:opacity-40 disabled:cursor-not-allowed active:scale-98"
                >
                  {busy ? (
                    <>
                      <RefreshCw size={13} className="animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={13} />
                      <span>Verify &amp; Activate Access</span>
                    </>
                  )}
                </button>
              </form>

              {/* Mini Collapsible UTR Helper */}
              <div className="text-[10px]">
                <button
                  type="button"
                  onClick={() => setShowGuide(!showGuide)}
                  className="text-slate-400 hover:text-slate-200 flex items-center gap-1 transition"
                >
                  <Info size={11} className="text-sky-400" />
                  <span>Where to find 12-digit UTR in PhonePe/GPay?</span>
                  {showGuide ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                </button>
                {showGuide && (
                  <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/5 text-slate-300 space-y-0.5 text-[9px]">
                    <div>• <strong>Google Pay:</strong> View payment &gt; look for <strong>UPI transaction ID</strong></div>
                    <div>• <strong>PhonePe:</strong> View details &gt; look for <strong>UTR</strong> or <strong>Bank Ref No</strong></div>
                    <div>• <strong>Paytm:</strong> View receipt &gt; look for <strong>UPI Ref No</strong></div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div role="alert" className="p-2 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-1.5">
          <AlertCircle size={13} className="shrink-0 text-rose-400" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
